import { getFormProps, getInputProps, useForm } from '@conform-to/react';
import { getZodConstraint, parseWithZod } from '@conform-to/zod';
import { json, type ActionFunctionArgs, type MetaFunction } from '@remix-run/cloudflare';
import { Form, Link, useActionData, useSearchParams } from '@remix-run/react';
import { z } from 'zod';
import { saltPassword } from '~/backend/controllers/authController';
import { ErrorList, Field } from '~/my-components/forms';
import { StatusButton } from '~/my-components/status-button';
import { EmailSchema, PasswordSchema } from '~/types';
import { createStorage, createUserSession, getRedirectToFromRequest, updateSessionAndRedirect } from '~/utils/auth.server';
import { getPrisma } from '~/utils/db.server';
import { useIsPending } from '~/utils/misc';
import { myUuid } from '~/utils/myUuid';

const SignupSchema = z.object({
  email: EmailSchema,
  password: PasswordSchema,
});

export async function action({ request, context }: ActionFunctionArgs) {
  const formData = await request.formData();
  const prisma = getPrisma({ context });

  const submission = await parseWithZod(formData, {
    schema: SignupSchema.superRefine(async (data, ctx) => {
      const existingUser = await prisma.user.findUnique({
        where: { email: data.email },
        select: { id: true },
      });
      if (existingUser) {
        ctx.addIssue({
          path: ['email'],
          code: z.ZodIssueCode.custom,
          message: 'A user already exists with this email',
        });
        return;
      }
    }),
    async: true,
  });
  if (submission.status !== 'success') {
    return json({ result: submission.reply() }, { status: submission.status === 'error' ? 400 : 200 });
  }
  const redirectTo = getRedirectToFromRequest(request);

  const saltedPassword = await saltPassword(submission.value.password);

  const response = await prisma.user.create({
    data: {
      id: myUuid(),
      email: submission.value.email,
      password: saltedPassword,
      key: myUuid(),
    },
  });
  const storage = createStorage(context);
  const session = await createUserSession(response.id, context, storage);

  return updateSessionAndRedirect(storage, session, () => {}, redirectTo);
}

export const meta: MetaFunction = () => {
  return [{ title: 'Sign Up | Deliberate Time' }];
};

export default function SignupRoute() {
  const actionData = useActionData<typeof action>();
  const isPending = useIsPending();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirectTo');

  const [form, fields] = useForm({
    id: 'signup-form',
    constraint: getZodConstraint(SignupSchema),
    lastResult: actionData?.result,
    onValidate({ formData }) {
      const result = parseWithZod(formData, { schema: SignupSchema });
      return result;
    },
    shouldRevalidate: 'onBlur',
  });

  return (
    <div className="container flex flex-col justify-center pb-32 pt-20">
      <div className="text-center">
        <h1 className="text-h1">Create your account.</h1>
      </div>
      <div className="mx-auto mt-8 min-w-full max-w-sm sm:min-w-[368px]">
        <Form method="POST" {...getFormProps(form)}>
          <Field
            labelProps={{
              htmlFor: fields.email.id,
              children: 'Email',
            }}
            inputProps={{
              ...getInputProps(fields.email, { type: 'email' }),
              autoFocus: true,
              autoComplete: 'email',
            }}
            errors={fields.email.errors}
          />
          <Field
            labelProps={{
              htmlFor: fields.password.id,
              children: 'Password',
            }}
            inputProps={{
              ...getInputProps(fields.password, { type: 'password' }),
              autoComplete: 'new-password',
            }}
            errors={fields.password.errors}
          />
          <ErrorList errors={form.errors} id={form.errorId} />
          <StatusButton
            className="w-full"
            status={isPending ? 'pending' : form.status ?? 'idle'}
            type="submit"
            disabled={isPending}
          >
            Submit
          </StatusButton>
        </Form>
        <p className="mt-4 text-center text-sm">
          Already have an account?{' '}
          <Link to={`/login?redirectTo=${encodeURIComponent(redirectTo ?? '/')}`} className="underline">
            Log in
          </Link>
        </p>
      </div>
    </div>
  );
}
